import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";

export default function ChangePasswordFirst() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    navigate("/login");
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Welcome to VayuCheck!");
      await refresh();
      const dest = user.role === "admin" ? "/dashboard/admin" :
                   user.role === "partner" ? "/dashboard/partner" :
                   "/dashboard/customer";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-md px-6 py-16" data-testid="change-password-first-page">
        <div className="border border-border bg-card p-8 md:p-10 rounded-2xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-4">
            <ShieldCheck className="w-4 h-4" /> Security · First sign-in
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Set a new password</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Your account was created by the VayuCheck team. Choose a personal password before you continue.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Temporary password (from email)</Label>
              <Input data-testid="fp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New password</Label>
              <Input data-testid="fp-new" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="Min 6 characters" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm new password</Label>
              <Input data-testid="fp-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-lg bg-secondary border-border h-11 mt-2" />
            </div>
            <Button data-testid="fp-submit" type="submit" disabled={submitting} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">
              <KeyRound className="w-4 h-4 mr-2" />
              {submitting ? "Saving…" : "Update password & continue"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
